import { useState, useCallback, memo } from 'react';
import { UploadCloud, Loader, CheckCircle, XCircle, FileText, Upload, Info, Keyboard } from 'lucide-react';
import XlsxMapper from './../../components/XlsxMapper';
import ActionButton from './../../components/ActionButton'; 
import { uploadAndValidateFile } from '../../api/BackendApi'; 

const MAX_SIZE_BYTES = 4 * 1024 * 1024; // 4MB
const ALLOWED_EXTENSIONS = ['.xlsx', '.xls', '.csv'];

/**
 * Validates the file based on size and extension.
 * @param {File} file - The file to validate.
 * @returns {string|null} - An error message string if invalid, or null if valid.
 */
const validateFile = (file) => {
  if (!file) return 'Nebyl vybrán žádný soubor.';

  // Size Check
  if (file.size > MAX_SIZE_BYTES) {
    return `Soubor je příliš velký (${(file.size / 1024 / 1024).toFixed(2)} MB). Maximální povolená velikost je 4 MB.`;
  }

  // File type Check
  const fileName = file.name;
  const lastDotIndex = fileName.lastIndexOf('.');
  if (lastDotIndex === -1) {
    return `Soubor nemá příponu. Povolené formáty: ${ALLOWED_EXTENSIONS.join(', ')}`;
  }
  
  const fileExtension = fileName.slice(lastDotIndex).toLowerCase(); 
  if (!ALLOWED_EXTENSIONS.includes(fileExtension)) {
    return `Nepovolený typ souboru (${fileExtension}). Povolené formáty jsou: ${ALLOWED_EXTENSIONS.join(', ')}`;
  }

  return null; // No error
};

const ANSWER_COLUMNS_COUNT = 10;
const createEmptyManualRow = () => ({
  id: '',
  answers: Array(ANSWER_COLUMNS_COUNT).fill(''),
});

const InputModeToggle = memo(({ activeMode, onModeChange }) => (
  <div className="flex w-full mb-4 rounded-lg bg-gray-200 p-1">
    <button
      onClick={() => onModeChange('file')}
      className={`flex-1 flex items-center justify-center p-2 rounded-md font-semibold transition-all ${
        activeMode === 'file'
          ? 'bg-white text-blue-600 shadow-sm'
          : 'bg-transparent text-gray-600 hover:bg-gray-100'
      }`}
    >
      <Upload size={18} className="mr-2" />
      Nahrát soubor
      <div className="relative group flex items-center ml-2">
        <Info size={16} className="text-gray-400 cursor-help" />
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 p-3 bg-gray-800 text-white text-xs rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity duration-300 z-10">
          Povolené formáty: .xlsx, .xls, .csv.
          <br />
          Maximální velikost souboru je 4 MB.
          <br />
          Maximální počet odpovědí je 25000.
          <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-x-4 border-x-transparent border-t-4 border-t-gray-800"></div>
        </div>
      </div>
    </button>

    <button
      onClick={() => onModeChange('paste')}
      className={`flex-1 flex items-center justify-center p-2 rounded-md font-semibold transition-all ${
        activeMode === 'paste'
          ? 'bg-white text-blue-600 shadow-sm'
          : 'bg-transparent text-gray-600 hover:bg-gray-100'
      }`}
    >
      <FileText size={18} className="mr-2" />
      Vložit text
    </button>

    <button
      onClick={() => onModeChange('manual')}
      className={`flex-1 flex items-center justify-center p-2 rounded-md font-semibold transition-all ${
        activeMode === 'manual'
          ? 'bg-white text-blue-600 shadow-sm'
          : 'bg-transparent text-gray-600 hover:bg-gray-100'
      }`}
    >
      <Keyboard size={18} className="mr-2" />
      Psát jednotlivě
    </button>
  </div>
));

const EditableTable = memo(({ tableData, onTableEdit }) => (
  <div className="w-full mt-4 space-y-3">
    <h3 className="text-lg font-semibold text-gray-800">Náhled a úprava dat</h3>
    <p className="text-sm text-gray-600">
        Zde můžete upravit zpracovaný text. Po dokončení úprav klikněte na "Zkontrolovat data".
    </p>
    <div className="max-h-80 overflow-y-auto border border-gray-300 rounded-lg">
      <table className="min-w-full bg-white rounded-lg shadow-sm">
        <thead className="bg-gray-200 text-gray-700 sticky top-0">
            <tr>
                <th className="w-1/3 py-2 px-4 text-left font-semibold">ID respondenta</th>
                <th className="w-2/3 py-2 px-4 text-left font-semibold">Odpověď</th>
            </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {tableData.map((row, index) => (
            <tr key={index} className="hover:bg-gray-50">
              <td className="p-1">
                <input
                  type="text"
                  value={row.id}
                  onChange={(e) => onTableEdit(index, 'id', e.target.value)}
                  className="w-full p-2 border border-transparent rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-transparent focus:bg-white"
                />
              </td>
              <td className="p-1">
                <input
                  type="text"
                  value={row.answer}
                  onChange={(e) => onTableEdit(index, 'answer', e.target.value)}
                  className="w-full p-2 border border-transparent rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-transparent focus:bg-white"
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
));

const StatusDisplay = memo(({ isUploading, status, message, preview, error }) => {
  if (isUploading) {
    return (
      <div className="flex items-center text-blue-600 font-semibold">
        <Loader className="h-6 w-6 mr-2 animate-spin" />
        Kontrola souboru...
      </div>
    );
  }
  
  if (status === 'ok') {
    return (
      <div className="w-full h-full flex flex-col">
          <div className="flex items-center text-green-600 mb-4">
              <CheckCircle className="h-8 w-8 mr-3" />
              <h3 className="text-2xl font-bold">Data jsou v pořádku.</h3>
          </div>
          <p className="text-gray-600 mb-4">{message}</p>
          <p className="text-gray-600 mb-4">Zde je náhled prvních 10 odpovědí. Pokud náhled neodpovídá vašim datům, zkuste upravit jejich formát a nahrajte je znovu.</p>
          <div className="flex-grow bg-gray-50 p-4 rounded-lg border overflow-y-auto">
            <table className="min-w-full bg-white rounded-md shadow-sm">
              <thead className="bg-gray-200 text-gray-700">
                <tr>
                  <th className="py-2 px-4 text-left font-semibold">ID respondenta</th>
                  <th className="py-2 px-4 text-left font-semibold">Odpověď</th>
                </tr>
              </thead>
              <tbody>
                {preview &&
                  preview.map(([respondent_id, answer], index) => (
                    <tr
                      key={index}
                      className="border-b hover:bg-gray-50 transition-colors"
                    >
                      <td className="py-2 px-4">{respondent_id}</td>
                      <td className="py-2 px-4 text-blue-600 font-medium">{answer}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
      </div>
    );
  }
  
  if (status === 'error') {
    return (
      <div className="flex flex-col items-center text-red-600 font-semibold w-full">
        <div className="flex items-center mb-2">
           <XCircle className="h-6 w-6 mr-2" />
           Chyba souboru.
        </div>
        <p className="text-sm font-normal text-gray-700 mt-1 px-4 max-w-md text-center leading-relaxed whitespace-normal">{error}</p>
      </div>
    );
  }

  return null;
});

const ManualAnswersTable = memo(({ rows, onEdit, onAddRow, onRemoveRow }) => (
  <div className="w-full space-y-3">
    <p className="text-sm text-gray-600">
      Vyplňte ID respondenta a jeho odpovědi. Prázdné odpovědi se přeskočí.
    </p>

    <div className="max-h-96 overflow-auto border border-gray-300 rounded-lg">
      <table className="min-w-[2000px] bg-white rounded-lg shadow-sm">
        <thead className="bg-gray-200 text-gray-700 sticky top-0">
          <tr>
            <th className="w-40 py-2 px-3 text-left font-semibold">
              ID respondenta
            </th>

            {Array.from({ length: ANSWER_COLUMNS_COUNT }, (_, index) => (
              <th key={index} className="w-80 py-2 px-3 text-left font-semibold">
                Odpověď {index + 1}
              </th>
            ))}

            <th className="w-24 py-2 px-3 text-left font-semibold">
              Akce
            </th>
          </tr>
        </thead>

        <tbody className="divide-y divide-gray-200">
          {rows.map((row, rowIndex) => (
            <tr key={rowIndex} className="hover:bg-gray-50">
              <td className="p-1">
                <input
                  type="text"
                  value={row.id}
                  onChange={(e) => onEdit(rowIndex, 'id', null, e.target.value)}
                  placeholder="např. t8s_45"
                  className="w-full p-2 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </td>

              {row.answers.map((answer, answerIndex) => (
                <td key={answerIndex} className="p-1">
                  <input
                    type="text"
                    value={answer}
                    onChange={(e) =>
                      onEdit(rowIndex, 'answer', answerIndex, e.target.value)
                    }
                    placeholder={`Odpověď ${answerIndex + 1}`}
                    className="w-full p-2 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </td>
              ))}

              <td className="p-1">
                <button
                  type="button"
                  onClick={() => onRemoveRow(rowIndex)}
                  disabled={rows.length === 1}
                  className="text-sm text-red-600 disabled:text-gray-400 disabled:cursor-not-allowed"
                >
                  Smazat
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>

    <button
      type="button"
      onClick={onAddRow}
      className="w-full py-2 px-4 rounded-lg bg-gray-200 text-gray-800 font-semibold hover:bg-gray-300"
    >
      Přidat respondenta
    </button>
  </div>
));

const FileUploadStep = ({ setFileId, question, onBack, onNext }) => {
  const [fileToUpload, setFileToUpload] = useState(null); // The final CSV ready for backend
  const [rawXlsxFile, setRawXlsxFile] = useState(null);   // Original Excel file for the mapper
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState(null); 
  const [validationError, setValidationError] = useState(null);
  const [responseMessage, setResponseMessage] = useState(null);
  const [responsePreview, setResponsePreview] = useState(null);
  const [activeInputMode, setActiveInputMode] = useState('file'); // 'file' or 'paste'
  const [pastedText, setPastedText] = useState('');
  const [tableData, setTableData] = useState([]); // Array of { id: string, answer: string }
  const [manualRows, setManualRows] = useState([createEmptyManualRow()]);

  const handleDrag = (e, status) => {
    e.preventDefault(); e.stopPropagation(); setIsDragging(status);
  };

  // Generalized handler for file selection (Drop or Input)
  const processSelectedFile = (newFile) => {
    // Reset previous states
    setUploadStatus(null);
    setResponseMessage(null);
    setResponsePreview(null);
    
    const error = validateFile(newFile);
    if (error) {
      setFileToUpload(null);
      setRawXlsxFile(null);
      setUploadStatus('error');
      setValidationError(error);
      return;
    }

    setRawXlsxFile(newFile); // Show Mapper
    setFileToUpload(null); 
  };

  // Called automatically by XlsxMapper when selection changes
  const handleMappedCsvChange = useCallback((csvFile) => {
    setFileToUpload(csvFile);
    // Always reset status when the file data changes to force re-validation if the user clicks again
    setUploadStatus(null); 
  }, []);

  // Paste Logic
  const handleProcessText = useCallback(() => {
    const lines = pastedText.split('\n');
    const newTableData = lines
    .filter(line => line.trim().length > 0)
    .flatMap(line => {
      const parts = line.split('\t');

      // Pokud je jen jeden sloupec, bereme ho jako odpověď bez ID.
      // Tedy například řádek "samsung" => id: "", answer: "samsung"
      if (parts.length === 1) {
        const answer = parts[0].trim();
        return answer ? [{ id: '', answer }] : [];
      }

      // První sloupec je ID, ale může být prázdný.
      // Například řádek "\tsamsung\tnokia" => id: "", answers: ["samsung", "nokia"]
      const id = parts[0].trim();

      const answers = parts
        .slice(1)
        .map(p => p.trim())
        .filter(p => p.length > 0);

      return answers.map(answer => ({
        id,
        answer,
      }));
    });
    setTableData(newTableData);
    setFileToUpload(null); 
    setRawXlsxFile(null);
    setUploadStatus(null);
  }, [pastedText]);

  // Backend Submission
  const handleConfirm = async () => {
    setIsUploading(true);
    setUploadStatus(null);
    setValidationError(null);

    try {
        let finalFile = fileToUpload;

        // If in paste mode, create synthetic file
        // If in paste/manual mode, create synthetic CSV file
        if (activeInputMode === 'paste' || activeInputMode === 'manual') {
          const sourceData =
            activeInputMode === 'paste'
              ? tableData
              : manualTableData;

          if (sourceData.length === 0) {
            throw new Error(
              activeInputMode === 'paste'
                ? 'Nejprve vložte text.'
                : 'Nejprve vyplňte alespoň jednu odpověď.'
            );
          }

          const csvContent = sourceData
            .map((row) => {
              const id = row.id || '';
              const answer = row.answer || '';

              return `"${id.replace(/"/g, '""')}","${answer.replace(/"/g, '""')}"`;
            })
            .join('\n');

          finalFile = new File([csvContent], "paste_data.csv", {
            type: "text/csv",
          });

          setFileToUpload(finalFile);
        }

        if (!finalFile) throw new Error('Nebyl vybrán žádný soubor.');

        window.dataLayer.push({ event: 'step_data' });
        const response = await uploadAndValidateFile(finalFile);
        setUploadStatus('ok');
        setResponseMessage(response.message);
        setResponsePreview(response.preview);
        setFileId(response.file_id);
    } catch (error) {
        setUploadStatus('error');
        setValidationError(error.message);
    } finally {
        setIsUploading(false);
    }
  };

  const manualTableData = manualRows.flatMap((row) => {
    const id = row.id.trim();

    return row.answers
      .map((answer) => answer.trim())
      .filter((answer) => answer.length > 0)
      .map((answer) => ({
        id,
        answer,
      }));
  });
  const hasManualData = manualTableData.length > 0;
  const handleManualEdit = (rowIndex, field, answerIndex, value) => {
    setManualRows((currentRows) =>
      currentRows.map((row, index) => {
        if (index !== rowIndex) return row;

        if (field === 'id') {
          return {
            ...row,
            id: value,
          };
        }

        const newAnswers = [...row.answers];
        newAnswers[answerIndex] = value;

        return {
          ...row,
          answers: newAnswers,
        };
      })
    );

    setUploadStatus(null);
  };
  const handleAddManualRow = () => {
    setManualRows((currentRows) => [...currentRows, createEmptyManualRow()]);
    setUploadStatus(null);
  };
  const handleRemoveManualRow = (rowIndex) => {
    setManualRows((currentRows) =>
      currentRows.length === 1
        ? currentRows
        : currentRows.filter((_, index) => index !== rowIndex)
    );

    setUploadStatus(null);
  };

  const handleTextareaTab = (e) => {
    if (e.key !== "Tab") return;

    e.preventDefault();

    const textarea = e.target;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;

    const newValue =
      pastedText.substring(0, start) +
      "\t" +
      pastedText.substring(end);

    setPastedText(newValue);

    // Po aktualizaci hodnoty vrátíme kurzor za vložený tabulátor
    setTimeout(() => {
      textarea.selectionStart = textarea.selectionEnd = start + 1;
    }, 0);
  };

  return (
    <div className="flex flex-col items-center w-full flex-grow">
      <div className="w-full text-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Nahrajte odpovědi respondentů</h2>
        <p className="">Krok 2</p>
        <div className="items-start gap-2 rounded-lg mb-4 text-gray-500">
          <p>Otázka: '{question}'</p>
        </div>
      </div>

      <div className="w-full mb-4">
        <div className="flex items-start gap-2 rounded-lg mb-4 text-gray-500">
          <Info className="w-5 h-5 flex-shrink-0 text-gray-500 mt-0.5" />
          <p>Můžete nahrát celý soubor s odpověďmi nebo vložit pouze odpovědi pomocí copy&paste.</p>
        </div>
        <InputModeToggle
          activeMode={activeInputMode}
          onModeChange={(m) => {
            setActiveInputMode(m);
            setRawXlsxFile(null);
            setFileToUpload(null);
            setUploadStatus(null);
            setValidationError(null);
            setResponseMessage(null);
            setResponsePreview(null);
          }}
        />

        {/* FILE INPUT MODE */}
        {activeInputMode === 'file' && (
          <>
            <div
              onDragEnter={(e) => handleDrag(e, true)}
              onDragLeave={(e) => handleDrag(e, false)}
              onDragOver={(e) => handleDrag(e, true)}
              onDrop={(e) => {
                handleDrag(e, false);
                if (e.dataTransfer.files?.[0]) processSelectedFile(e.dataTransfer.files[0]);
              }}
              className={`relative flex flex-col items-center justify-center w-full h-32 p-4 border-2 border-dashed rounded-lg transition-all duration-300 ${
                isDragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300 bg-gray-50'
              }`}
            >
              <input
                type="file"
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                onChange={(e) => {
                  if (e.target.files?.[0]) {
                    processSelectedFile(e.target.files[0]);
                    e.target.value = null;
                  }
                }}
                disabled={isUploading}
                accept={ALLOWED_EXTENSIONS.join(',')}
              />

              {rawXlsxFile || fileToUpload ? (
                <div className="text-center">
                  <p className="font-semibold text-gray-800">
                    Vybráno: {rawXlsxFile ? rawXlsxFile.name : fileToUpload.name}
                  </p>
                  <p className="text-xs text-blue-600 cursor-pointer hover:underline">
                    Klikněte nebo přetáhněte pro změnu
                  </p>
                </div>
              ) : (
                <div className="text-center text-gray-500">
                  <UploadCloud className="mx-auto h-8 w-8 text-gray-400" />
                  <p className="mt-1 font-semibold">
                    Tažením přesuňte soubor sem nebo klikněte a vyberte soubor z adresáře.
                  </p>
                </div>
              )}
            </div>

            {rawXlsxFile && (
              <XlsxMapper file={rawXlsxFile} onChange={handleMappedCsvChange} />
            )}
          </>
        )}

        {activeInputMode === 'paste' && (
          <div className="w-full">
            <textarea
              rows="8"
              className="w-full p-3 border border-gray-300 rounded-lg"
              placeholder={`Kopírujte nebo vkládejte text bez názvů sloupců, v první sloupci očekáváme id respondenta (ne odpověď), následují odpovědi respondenta odděleny tabulátorem. Na novém řádku další respondent. Například zkopírované buňky z excelu.

Příklad:
t8s_45\tsamsung\tnokie\tmotorola
t8s_62\tapple`}
              value={pastedText}
              onChange={(e) => setPastedText(e.target.value)}
              onKeyDown={handleTextareaTab}
              disabled={isUploading}
            />

            <ActionButton
              onClick={handleProcessText}
              disabled={!pastedText.trim() || isUploading}
              className="w-full mt-2"
            >
              Náhled textu
            </ActionButton>

            {tableData.length > 0 && !isUploading && uploadStatus !== 'ok' && (
              <EditableTable
                tableData={tableData}
                onTableEdit={(idx, k, v) =>
                  setTableData((d) => {
                    const n = [...d];
                    n[idx][k] = v;
                    return n;
                  })
                }
              />
            )}
          </div>
        )}

        {activeInputMode === 'manual' && (
          <ManualAnswersTable
            rows={manualRows}
            onEdit={handleManualEdit}
            onAddRow={handleAddManualRow}
            onRemoveRow={handleRemoveManualRow}
          />
        )}

        <div className="mt-4 flex justify-center min-h-[2rem]">
          <StatusDisplay isUploading={isUploading} status={uploadStatus} message={responseMessage} preview={responsePreview} error={validationError} />
        </div>
      </div>
      
      {/* ACTIONS */}
      <div className="flex flex-col gap-4 w-full">
        {/* Check Button: Visible if we have a ready file (CSV or Processed XLSX) OR Paste Data */}
        {(
          (activeInputMode === 'file' && fileToUpload) ||
          (activeInputMode === 'paste' && tableData.length > 0) ||
          (activeInputMode === 'manual' && hasManualData)
        ) && uploadStatus !== 'ok' && (
          <ActionButton onClick={handleConfirm} disabled={isUploading} className="w-full">
            {isUploading ? 'Zpracovávám...' : 'Zkontrolovat data'}
          </ActionButton>
        )}
        
        <div className="flex gap-4 w-full pt-4 border-t border-gray-200">
          <button onClick={onBack} className="w-1/2 bg-gray-200 text-gray-800 font-semibold py-3 px-4 rounded-lg hover:bg-gray-300">Zpět</button>
          <ActionButton onClick={onNext} disabled={uploadStatus !== 'ok'} className="w-1/2">Pokračovat</ActionButton>
        </div>
      </div>
    </div>
  );
};
export default FileUploadStep;